import { getAuth } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { EditableTitle } from 'src/components/EditableTitle';
import { Heading } from 'src/components/Heading';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { useNavigation } from 'src/contexts/NavigationContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { logoutHelper } from 'src/helpers/logoutHelper';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Action } from 'src/interfaces/action.interface';
import { Agent } from 'src/interfaces/agent.interface';
import { Call } from 'src/interfaces/call.interface';
import { Number } from 'src/interfaces/number.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { AgentStub } from 'src/stubs/agent.stub';
import { processPhoneNumber } from 'src/utils/number';
import { isValidHttpUrl } from 'src/utils/url';
import { InboundCallsForm } from './components/InboundCallsForm';
import { useAgentActions } from './contexts/AgentActionsContext';
import { TabsAgent } from './TabsAgent';

export type ManageAgentProps = {
  editing?: boolean;
};

export type LocalAction = Action & {
  originalId?: string;
};

export const ManageAgent = ({ editing = false }: ManageAgentProps) => {
  const { get, patch, post } = useAuthedRequest();
  const { getActionById, addAction, getActionsToSave, resetActions } =
    useAgentActions();

  const auth = getAuth();
  const { id: editId } = useParams();

  const [agent, setAgent] = useState<Agent>(AgentStub as Agent);
  const [agentWithoutModifications, setAgentWithoutModifications] =
    useState<Agent | null>();
  const [agentChanged, setAgentChanged] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [agentLoading, setAgentLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [attachedPrompt, setAttachedPrompt] = useState<Prompt>();
  const [fetchedActions, setFetchedActions] = useState<string[]>([]);
  const [agentNumbers, setAgentNumbers] = useState<Number[]>([]);
  const [modalAgentNumbersOpen, setModalAgentNumbersOpen] = useState(false);
  const [isEditingAgentDetails, setIsEditingAgentDetails] = useState(false);
  const [selectedNumbers, setSelectedNumbers] = useState<Number[]>([]);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [calls, setCalls] = useState<Call[]>([]);

  const [contextEndpoint, setContextEndpoint] = useState<string>('');
  const [inboundCallActionButton, setInboundCallActionButton] =
    useState<React.ReactNode>(null);
  const [activeTab, setActiveTab] = useState(0);

  const navigate = useNavigate();
  const notification = useNotification();
  const { setBlocked, navigateWithBlocking } = useNavigation();

  // Set up navigation blocking based on agent changes
  useEffect(() => {
    setBlocked(agentChanged);

    return () => {
      setBlocked(false);
    };
  }, [agentChanged, setBlocked]);

  const handleNameChange = (name: string) => {
    setAgent((prevAgent) => ({ ...prevAgent, name, label: name }));
  };

  const handleDescriptionChange = (description: string) => {
    setAgent((prevAgent) => ({ ...prevAgent, description }));
  };

  const agentName = useMemo(() => agent.name || '', [agent.name]);
  const agentDescription = useMemo(
    () => agent.description || '',
    [agent.description],
  );

  const saveAgent = async () => {
    const url = editing ? `/agents/${editId}` : '/agents';

    // Destructure to drop legacy post_call_email and post_call_sms fields
    const {
      webhook,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      post_call_email,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      post_call_sms,
      post_call_prompt,
      post_call_analysis_items,
      ...restAgent
    } = agent as Agent & {
      post_call_email?: unknown;
      post_call_sms?: unknown;
    };

    // Use the context function to get actions to save
    const actionsToSave = getActionsToSave();

    const skillsToSave = agent.skills?.filter(
      (skill) => Object.keys(skill.config).length > 0,
    );

    const hasPostCallPrompt = !!post_call_prompt?.trim();

    let webhookToSave: string | object | null = null;

    if (typeof webhook === 'string' && webhook.length) {
      webhookToSave = webhook;
    } else if (
      typeof webhook !== 'string' &&
      webhook &&
      (webhook.id || Object.keys(webhook).length)
    ) {
      webhookToSave = webhook;
    }

    const clonedAgent = {
      ...restAgent,
      webhook: webhookToSave,
      actions: [...actionsToSave],
      label: agent.name || agent.label || 'Untitled Agent',
      skills: skillsToSave,
      ...(hasPostCallPrompt
        ? { post_call_prompt }
        : { post_call_analysis_items: post_call_analysis_items ?? [] }),
    };

    setAgentLoading(true);

    let createdAgent: Agent | null = null;

    if (editing) {
      await patch(url, clonedAgent);
      resetActions();
      setIsEditingAgentDetails(false);
      setAgentWithoutModifications(agent);
    } else {
      const canCreateAgent = async () => {
        const resp = await auth.currentUser?.getIdTokenResult();
        if (!resp) {
          notification.error(`Auth expired`);
          return logoutHelper();
        }
        const { id } = resp.claims;

        const plan = await get(`/users/${id}/plan`);
        const query = new URLSearchParams({ size: '1', page: '1' });
        const data = await get(`/agents?${query.toString()}`);

        if (plan) {
          const maxAgents = plan?.featureGates?.agent;
          if (maxAgents && data.total >= maxAgents) {
            notification.error(
              `The ${plan.name} plan is limited to ${maxAgents} agent(s). Please upgrade to add more agents`,
            );
            return false;
          }
        }
        return true;
      };

      if (!(await canCreateAgent())) {
        setAgentLoading(false);
        return;
      }

      createdAgent = await post(url, clonedAgent);
      resetActions();
      setIsEditingAgentDetails(false);
    }

    notification.success(
      `Agent ${editing ? 'updated' : 'created'} successfully`,
    );

    if (!editing && createdAgent) {
      navigate(createdAgent?.id ? `/agent/edit/${createdAgent.id}` : '/agents');
    }

    if (editing && editId) {
      await populateAgent();
    }

    setAgentLoading(false);
  };

  const saveAndExit = async () => {
    await saveAgent();
    navigate('/agents');
  };

  const fetchCalls = useCallback(
    async (agentId: string) => {
      if (!agentId) return;

      try {
        const filters = {
          agent: agentId,
          from_number: '',
          to_number: '',
          start_time: null,
          end_time: null,
        };
        const data = await get(
          `/calls?page=1&size=1&filters=${encodeURIComponent(
            JSON.stringify(filters),
          )}`,
        );
        setCalls(data.items);
        return data.items;
      } catch (error) {
        console.error('Failed to fetch calls:', error);
      }
    },
    [get],
  );

  const fetchActionsForAgent = useCallback(
    async (actions: string[]) => {
      for (const action of actions) {
        if (!fetchedActions.includes(action)) {
          try {
            const actionToAdd = await getActionById(
              action as unknown as string,
            );
            if (actionToAdd) addAction(actionToAdd as unknown as Action);
            setFetchedActions((prev) => [...prev, action]);
          } catch (error) {
            console.error(`Error fetching action ${action}:`, error);
          }
        }
      }
    },
    [fetchedActions, getActionById, addAction],
  );

  const populateAgent = useCallback(async () => {
    setIsInitializing(true);

    try {
      const [agentData, numbers] = await Promise.all([
        get(`/agents/${editId}`),
        get(`/numbers?filters=${JSON.stringify({ agent: editId })}`),
      ]);

      let promptData: Prompt | string | null = agentData.prompt;
      if (typeof agentData.prompt === 'string') {
        promptData = await get(`/prompts/${agentData.prompt}`);
      }

      // Destructure to drop legacy post_call_email and post_call_sms fields from API response
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { post_call_email, post_call_sms, ...agentDataWithoutLegacy } =
        agentData;

      const agentInitialData = {
        ...agentDataWithoutLegacy,
        name: agentData.name || agentData.label || '',
        llm_fallback: agentData.llm_fallback || AgentStub.llm_fallback,
        prompt: promptData,
        voice: agentData.voice?.id || '',
        webhook: agentData.webhook?.id || '',
        actions: agentData.actions.map((action: Action) => action.id),
        transcriber: agentData.transcriber,
      };

      setAgent(agentInitialData);
      setAgentWithoutModifications(agentInitialData);

      setContextEndpoint(agentData.context_endpoint || '');

      setAttachedPrompt(promptData as Prompt);
      setAgentNumbers(numbers?.items || []);
      setSelectedNumbers(numbers?.items?.map((number: Number) => number) || []);

      await fetchActionsForAgent(
        agentData.actions.map((action: Action) => action.id),
      );

      await fetchCalls(agentData.id);
    } catch (error) {
      console.error('Error loading agent:', error);
      notification.error('Failed to load agent data');
      navigate('/agents');
    } finally {
      setIsInitializing(false);
    }
  }, [editId, get, navigate, notification, fetchActionsForAgent, fetchCalls]);

  const populateAgentNumbers = async () => {
    const numbers = await get(
      `/numbers?filters=${JSON.stringify({ agent: editId })}`,
    );
    setAgentNumbers(numbers?.items || []);
    setSelectedNumbers(numbers?.items?.map((number: Number) => number) || []);
  };

  const updateAgentInboundSettings = async (numbers?: Number[]) => {
    const numbersToUse = numbers || selectedNumbers;

    if (contextEndpoint.length > 0 && !isValidHttpUrl(contextEndpoint)) {
      notification.error('Please enter a valid URL');
      return;
    }

    setAgentLoading(true);

    const currentNumberIds = agentNumbers.map((number) => number);

    const newNumberIds = numbersToUse || [];

    const idsToRemove = currentNumberIds.filter(
      (number) => !newNumberIds.includes(number),
    );
    await Promise.all(
      idsToRemove.map((number) =>
        patch(
          `/numbers/${number.id}?phone_number=%2B${number.number.replace(/[^0-9]/g, '')}`,
          {
            inbound_agent: null,
          },
        ),
      ),
    );

    const idsToAdd = newNumberIds.filter(
      (number) => !currentNumberIds.includes(number),
    );
    await Promise.all(
      idsToAdd.map((number) =>
        patch(
          `/numbers/${number.id}?phone_number=%2B${number.number.replace(/[^0-9]/g, '')}`,
          {
            inbound_agent: editId,
          },
        ),
      ),
    );

    await populateAgentNumbers();
    setModalAgentNumbersOpen(false);

    if (contextEndpoint.length > 0) {
      if (!isValidHttpUrl(contextEndpoint)) {
        notification.error('Please enter a valid URL');
        return;
      }

      await patch(`/agents/${editId}`, {
        context_endpoint: contextEndpoint,
      });

      setAgent((prevAgent) => ({
        ...prevAgent,
        context_endpoint: contextEndpoint,
      }));
    } else {
      await patch(`/agents/${editId}`, {
        context_endpoint: null,
      });

      setAgent((prevAgent) => ({
        ...prevAgent,
        context_endpoint: null,
      }));
    }

    notification.success(`Agent inbound settings updated successfully`);

    setAgentLoading(false);
  };

  useEffect(() => {
    setDisabled(
      !agent.voice ||
        !agent.prompt ||
        String(agent.prompt) === '' ||
        (agent.prompt as Prompt).content === '',
    );

    setAgentChanged(
      JSON.stringify(agent) !== JSON.stringify(agentWithoutModifications),
    );
  }, [agent, agentWithoutModifications]);

  useEffect(() => {
    if (editId) {
      populateAgent();
    } else {
      setIsInitializing(false);
    }
  }, [editId, populateAgent]);

  useEffect(() => {
    async function populatePromptContent() {
      if (typeof agent?.prompt === 'string' && agent.prompt) {
        const prompt = await get(`/prompts/${agent.prompt}`);
        setAgent((prevAgent) => {
          if (typeof prevAgent.prompt === 'string') {
            return { ...prevAgent, prompt };
          }
          return prevAgent;
        });
      }
    }

    populatePromptContent();
  }, [agent?.prompt, get]);

  useEffect(() => {
    if (!editing) {
      setIsEditingAgentDetails(true);
    }
  }, [editing]);

  if (isInitializing) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loading size="8" />
      </div>
    );
  }

  const formatAgentNumbers = (numbers: Number[]) => {
    if (numbers.length > 0) {
      return numbers
        .map((number) => processPhoneNumber(number.number))
        .join(', ');
    }
    return 'No numbers assigned.';
  };

  return (
    <>
      <Heading
        className="border-b-0"
        title={
          <EditableTitle
            titleInputRef={titleInputRef}
            metadata={formatAgentNumbers(agentNumbers)}
            titlePlaceholder={editing ? 'Edit Agent' : 'New Agent'}
            subtitlePlaceholder="Add agent description"
            onNameChange={handleNameChange}
            onDescriptionChange={handleDescriptionChange}
            loading={agentLoading}
            isEditing={isEditingAgentDetails}
            setIsEditing={setIsEditingAgentDetails}
            name={agentName}
            description={agentDescription}
          />
        }
        fixedHeader
      >
        <div className="flex gap-2 mt-3">
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigateWithBlocking('/agents')}
          >
            Cancel
          </Button>

          <Can operation="update" subject={Subject.AGENTS}>
            <Button
              variant="outlined"
              size="small"
              onClick={saveAndExit}
              disabled={disabled || agentLoading}
            >
              Save & Exit
            </Button>
          </Can>

          <Can operation="update" subject={Subject.AGENTS}>
            <Button
              size="small"
              onClick={saveAgent}
              disabled={disabled || agentLoading}
            >
              {agentLoading ? 'Loading...' : 'Save'}
            </Button>
          </Can>
        </div>
      </Heading>

      <ContentWrapper className="p-0 h-auto">
        <TabsAgent
          agent={agent}
          setAgent={setAgent}
          setLoading={setAgentLoading}
          loading={agentLoading}
          defaultPrompt={attachedPrompt}
          finishAction={disabled ? undefined : saveAgent}
          setModalAgentNumbersOpen={setModalAgentNumbersOpen}
          calls={calls}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        {/* Setup Inbound Calls Modal */}
        <Modal
          title="Setup Inbound Calls"
          isOpen={modalAgentNumbersOpen}
          onClose={() => setModalAgentNumbersOpen(false)}
          actionButton={inboundCallActionButton}
        >
          <InboundCallsForm
            setInboundCallActionButton={setInboundCallActionButton}
            agentLoading={agentLoading}
            contextEndpoint={contextEndpoint}
            selectedNumbers={selectedNumbers}
            setSelectedNumbers={setSelectedNumbers}
            setContextEndpoint={setContextEndpoint}
            updateAgentInboundSettings={updateAgentInboundSettings}
            agent={agent}
          />
        </Modal>
      </ContentWrapper>
    </>
  );
};
